import { IArrival, OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { IMvpInventory } from '@/coreDomainsModule/entities/inventory';
import { ISalesResult } from '@/coreDomainsModule/entities/salesResults';
import { ArrivalFactory } from '@/coreDomainsModule/factories/ArrivalFactory';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { DateService } from '@/coreDomainsModule/services/dateService';
import {
  GetInventoryBusinessSuccessResponseInfo,
  GetInventorySuccessResponseInfo,
  Inventory,
  SummaryData,
} from '@/coreInfrastructureModule/axios/generated/inventoryManage';
import { IParentBusiness } from '../domains/entities/Business';
import { getRandomStr } from '@/businessRepository/idUtils';
import { ParentBusinessFactory } from '../domains/factories/ParentBusinessFactory';
import { RecommendOrderFactory } from '../domains/factories/RecommendOrderFactory';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { SummaryDataFactory } from '../domains/factories/SummaryDataFactory';
import { ISummaryData } from '../domains/entities/SummaryData';

/**
 * 在庫情報のadapter
 */
export class InventoryAdapter {
  /**
   * Inventory[]からnumberへ変換する
   * 在庫年月日が最新のものかつ、今月または先月、先々月の在庫情報を取得する(なければundefinedを返す)
   * @param receiveExecutionDate - 在庫年月日
   * @param response - inventory
   */
  private static adaptLatestInventory = (receiveExecutionDate: Date, response?: Inventory[]): Inventory | undefined => {
    if (!response) return undefined;
    const executionDate = DateService.convertDateToIDate(receiveExecutionDate);
    const nowDate = DateService.convertDateToIDate(new Date());
    const lastMonthDate = DateService.addMonthsToCurrentDate(-1);
    const lastLastMonthDate = DateService.addMonthsToCurrentDate(-2);

    // 在庫年月日の検証
    // NOTE: 在庫年月日が今月または先月、先々月でない場合にはundefinedを返す
    if (
      (executionDate.month !== nowDate.month || executionDate.year !== nowDate.year) &&
      (executionDate.month !== lastMonthDate.month || executionDate.year !== lastMonthDate.year) &&
      (executionDate.month !== lastLastMonthDate.month || executionDate.year !== lastLastMonthDate.year)
    ) {
      return undefined;
    }

    // 在庫年月日が年月ごとに最新の情報にソートする
    const sortedInventory = response.sort((a, b) => {
      if (a.targetYear === undefined || a.targetMonth === undefined || b.targetYear === undefined || b.targetMonth === undefined) return 0;

      if (a.targetYear > b.targetYear) {
        return -1; // 年が新しい方を前に
      } else if (a.targetYear < b.targetYear) {
        return 1; // 年が古い方を後ろに
      } else {
        // 年が同じ場合、月で比較
        return b.targetMonth - a.targetMonth;
      }
    });

    // 最新の在庫情報を取得する
    // NOTE: 在庫年月日を元に在庫情報を取得する
    for (const inventory of sortedInventory) {
      if (inventory.targetMonth === executionDate.month && inventory.targetYear === executionDate.year) {
        return inventory;
      }
    }

    // 今月または先月の在庫情報がない場合はundefinedを返す;
    return undefined;
  };

  /**
   * GetInventorySuccessResponseをIBusiness[]に変換する
   * @param info - GetInventorySuccessResponseInfo
   */
  public static adaptAggregateBusiness = (info: GetInventorySuccessResponseInfo): IParentBusiness[] => {
    /**
     * BEから来た入荷予定日をIDateに変換する
     * @param eta - BEから来た入荷予定日
     * @returns IDate型の入荷予定日
     */
    const _getDate = (eta: number | undefined) => (eta ? DateService.convertDateToIDate(new Date(eta)) : { year: 0, month: 0, day: 0 });

    /**
     * BEから来た発注作成日をDateに変換する
     * @param createdDate - BEから来た発注作成日
     * @returns Date型の発注作成日
     */
    const _getCreatedDate = (createdDate: number | undefined) =>
      createdDate ? DateService.convertUnixTimeToDate(createdDate / 1000) : new Date();

    const result: IParentBusiness[] = [];

    info.data?.forEach((aggregateBusiness) => {
      let userUniqueNumber: string | undefined;
      try {
        if (typeof aggregateBusiness.userCustomParameters === 'string' && aggregateBusiness.userCustomParameters) {
          const params = JSON.parse(aggregateBusiness.userCustomParameters);
          userUniqueNumber = params?.['顧客台帳ID'];
        }
      } catch (e) {
        console.error('Failed to parse userCustomParameters for aggregateBusinessId:', aggregateBusiness.aggregateBusinessId, e);
      }

      const business = ParentBusinessFactory.create({
        businessId: aggregateBusiness.aggregateBusinessId,
        businessName: aggregateBusiness.businessName,
        businessNumber: aggregateBusiness.businessNumber,
        warehouse: aggregateBusiness.warehouse,
        warehouseNumber: aggregateBusiness.warehouseNumber,
        supplierName: aggregateBusiness.supplierName,
        supplierNumber: aggregateBusiness.supplierNumber,
        package: aggregateBusiness.package?.toString(),
        unit: aggregateBusiness.unit,
        manufacturer: aggregateBusiness.manufacturer,
        note: aggregateBusiness.aggregateBusinessNote,
        forecastForReceiptName: undefined,
        inventory: aggregateBusiness.inventoryAtExecutionDate,
        inventoryConditionAtExecutionDate: aggregateBusiness.inventoryConditionAtExecutionDate,
        userUniqueNumber,
        businessFreeColumnValues: aggregateBusiness.businessFreeColumnValues?.map((item) => ({
          columnNo: item.columnNo,
          headerName: item.headerName,
          value: item.value,
          isMulti: item.isMulti,
          isConfigured: item.isConfigured,
          target: item.target,
        })),
        recommendEstimateTime: aggregateBusiness.recommendEstimateTime
          ? DateService.convertUnixTimeToDate(aggregateBusiness.recommendEstimateTime / 1000)
          : undefined,
        recommendArrivalValue: aggregateBusiness.recommendArrivalValue,
        stdInventoryIndices: {
          標準在庫指数: {
            min: aggregateBusiness.standardInventoryIndexMin ?? 0,
            max: aggregateBusiness.standardInventoryIndexMax ?? 0,
            option: {
              unit: aggregateBusiness.standardInventoryIndexUnit ?? '',
            },
          },
        },
        leadTime: aggregateBusiness.leadTime ?? 0,
        leadTimeUnit: aggregateBusiness.leadTimeUnit,
        inTransitDays: aggregateBusiness.inTransitDays ?? 0,
        MOQ: aggregateBusiness.moq,
        orderPackage: aggregateBusiness.orderPackage ?? 0,
        purchaseUnitPrice: aggregateBusiness.purchaseUnitPrice != null ? Number(aggregateBusiness.purchaseUnitPrice) : undefined,
        aggregateBusinessResponsibleUsers:
          aggregateBusiness.aggregateBusinessResponsibleUsers?.map((user) =>
            UserAccountFactory.create({ email: user.id, name: user.name })
          ) ?? [],
        executionDate: DateService.convertUnixTimeToDate(aggregateBusiness.executionDate ? aggregateBusiness.executionDate / 1000 : 0),
        arrivals: aggregateBusiness.arrivals?.map((arrival): IArrival => {
          return ArrivalFactory.create({
            arrivalId: arrival.arrivalId,
            amount: arrival.arrivalAmount,
            year: arrival.targetYear,
            month: arrival.targetMonth,
          });
        }),
        mvpInventory: aggregateBusiness.inventories?.map((inventory): IMvpInventory => {
          return {
            amount: inventory.inventoryAmount ?? 0,
            year: inventory.targetYear ?? 0,
            month: inventory.targetMonth ?? 0,
          };
        }),
        salesResults: aggregateBusiness.salesResults?.map((salesResult): ISalesResult => {
          return {
            amount: salesResult.salesResultAmount ?? 0,
            year: salesResult.targetYear ?? 0,
            month: salesResult.targetMonth ?? 0,
          };
        }),
        customerAnnounce: aggregateBusiness.customerAnnounces?.map((announce): IAnnounce => {
          return {
            amount: announce.customerForecastAmountTotal ?? 0,
            manageInitiatedYearMonth: {
              year: announce.customerForecastReceivedYear ?? 0,
              month: announce.customerForecastReceivedMonth ?? 0,
            },
            year: announce.customerForecastTargetYear ?? 0,
            month: announce.customerForecastTargetMonth ?? 0,
            createAt: undefined,
            updateAt: undefined,
          };
        }),
        supplierAnnounce: aggregateBusiness.supplierAnnounces?.map((announce: any): IAnnounce => {
          return {
            amount: announce.supplierForecastAmount ?? 0,
            manageInitiatedYearMonth: {
              year: announce.supplierForecastCreatedYear ?? 0,
              month: announce.supplierForecastCreatedMonth ?? 0,
            },
            year: announce.supplierForecastTargetYear ?? 0,
            month: announce.supplierForecastTargetMonth ?? 0,
            createAt: undefined,
            updateAt: undefined,
          };
        }),
        recommend: aggregateBusiness.orders?.map((order) => {
          if (order.orderStatus === undefined || Object.keys(OrderStatus).includes(order.orderStatus) === false)
            console.error(`OrderStatus is invalid. orderStatus: ${order.orderStatus}`);

          const createDate = _getCreatedDate(order.createdOrderDate);

          return {
            ...RecommendOrderFactory.create({
              arrivalId: order.orderId,
              crossDate: _getDate(order.estimatedTimeOfArrival),
              amount: order.orderAmount ?? 0,
              status: order.orderStatus as OrderStatus,
              year: createDate.getFullYear(),
              month: createDate.getMonth() + 1,
              day: createDate.getDate(),
              displayId: getRandomStr(),
              orderNumber: order.orderNumber,
              isFixOrdered: true, // DBにはそもそも確定発注しかいれないので
              updateAt: order.updateAt,
              partialDeliveries:
                order.partialDeliveries?.map((pd) => {
                  const pdCreatedDate = _getCreatedDate(pd.createdPartialDeliveryDate);
                  return ArrivalFactory.create({
                    arrivalId: pd.partialDeliveryId,
                    crossDate: _getDate(pd.partialDeliveryEstimatedTimeOfArrival),
                    amount: pd.partialDeliveryAmount ?? 0,
                    status: pd.partialDeliveryState as OrderStatus,
                    year: pdCreatedDate.getFullYear(),
                    month: pdCreatedDate.getMonth() + 1,
                    day: pdCreatedDate.getDate(),
                    number: pd.partialDeliveryNumber,
                    updateAt: pd.updateAt,
                  });
                }) ?? [],
            }),
          };
        }),
        updatedAt: aggregateBusiness.updateAt ? aggregateBusiness.updateAt / 1000 : undefined,
        createdAt: aggregateBusiness.createAt ? aggregateBusiness.createAt / 1000 : undefined,
      });
      result.push(business);
    });
    return result;
  };

  /**
   * GetInventoryBusinessSuccessResponseをIBusiness[]に変換する
   * @param info - GetInventoryBusinessSuccessResponseInfo
   */
  public static adaptBusiness = (info: GetInventoryBusinessSuccessResponseInfo): IBusiness[] => {
    const result: IBusiness[] = [];
    info.data?.forEach((business) => {
      const businessObj = BusinessFactory.create({
        businessId: business.businessId,
        customerNumber: business.customerNumber,
        customerName: business.customerName,
        salesDestinationNumber: business.salesDestinationNumber,
        salesDestinationName: business.salesDestinationName,
        forecastForReceiptName: business.forecastForReceiptName ?? undefined,
        endUser: business.endUser,
        salesUnitPrice: business.salesUnitPrice != null ? Number(business.salesUnitPrice) : undefined,
        note: business.baseBusinessNote,
        businessFreeColumnValues: business.businessFreeColumnValues?.map((item) => ({
          columnNo: item.columnNo,
          headerName: item.headerName,
          value: item.value,
          isMulti: item.isMulti,
          isConfigured: item.isConfigured,
          target: item.target,
        })),
        baseBusinessResponsibleUsers:
          business.baseBusinessResponsibleUsers?.map((user) =>
            UserAccountFactory.create({
              email: user.id,
              name: user.name,
            })
          ) ?? [],
        salesResults: business.salesResults?.map((salesResult) => {
          return {
            amount: (salesResult as any).salesResultAmountTotal ?? 0,
            year: (salesResult as any).salesResultTargetYear ?? 0,
            month: (salesResult as any).salesResultTargetMonth ?? 0,
          };
        }),
        customerAnnounce: business.customerAnnounces?.map((announce) => {
          return {
            amount: announce.customerForecastAmountTotal ?? 0,
            manageInitiatedYearMonth: {
              year: announce.customerForecastReceivedYear ?? 0,
              month: announce.customerForecastReceivedMonth ?? 0,
            },
            year: announce.customerForecastTargetYear ?? 0,
            month: announce.customerForecastTargetMonth ?? 0,
            createAt: undefined,
            updateAt: undefined,
          };
        }),
        updatedAt: business.updateAt ? business.updateAt / 1000 : undefined,
        createdAt: business.createAt ? business.createAt / 1000 : undefined,
      });
      result.push(businessObj);
    });
    return result;
  };

  /**
   * SummaryDataをISummaryDataに変換する
   * @param info - SummaryData
   */
  public static adaptSummaryData = (info?: SummaryData): ISummaryData => {
    const result = SummaryDataFactory.create({
      arrivalSummary: info?.arrivalSummary?.map((monthlyArrivalSummary) => ({
        arrivalAmountSummary: monthlyArrivalSummary.arrivalAmountSummary,
        targetMonth: monthlyArrivalSummary.targetMonth,
        targetYear: monthlyArrivalSummary.targetYear,
      })),
      customerForecastSummary: info?.customerForecastSummary?.map((monthlyCustomerForecastSummary) => ({
        customerForecastAmountSummary: monthlyCustomerForecastSummary.customerForecastAmountSummary,
        targetMonth: monthlyCustomerForecastSummary.targetMonth,
        targetYear: monthlyCustomerForecastSummary.targetYear,
        receivedMonth: monthlyCustomerForecastSummary.receivedMonth,
        receivedYear: monthlyCustomerForecastSummary.receivedYear,
      })),
      inventorySummary: info?.inventorySummary?.map((monthlyInventorySummary) => ({
        inventoryAmountSummary: monthlyInventorySummary.inventoryAmountSummary,
        targetMonth: monthlyInventorySummary.targetMonth,
        targetYear: monthlyInventorySummary.targetYear,
      })),
      orderSummary: info?.orderSummary?.map((monthlyOrderSummary) => ({
        preOrderAmountSummary: monthlyOrderSummary.preOrderAmountSummary,
        deliveredOrderAmountSummary: monthlyOrderSummary.deliveredOrderAmountSummary,
        targetMonth: monthlyOrderSummary.targetMonth,
        targetYear: monthlyOrderSummary.targetYear,
      })),
      salesResultSummary: info?.salesResultSummary?.map((monthlySalesResultSummary) => ({
        salesResultAmountSummary: monthlySalesResultSummary.salesResultAmountSummary,
        targetMonth: monthlySalesResultSummary.targetMonth,
        targetYear: monthlySalesResultSummary.targetYear,
      })),
    });
    return result;
  };
}
